// src/App.js

import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { motion, useScroll, useSpring } from 'framer-motion';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { HelmetProvider } from 'react-helmet-async';

// SEO
import SEO, { homepageSchema } from './components/SEO';

// Device Detection & Routing
import { DeviceProvider } from './shared/context/DeviceContext';
import DeviceRouter from './shared/components/DeviceRouter';

// Components
import SmoothScroll from './components/SmoothScroll';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Capabilities from './components/Capabilities';
// import ApproachSection from './components/approach/ApproachSection'; // ← ADD THIS
// import MeetTheTeam from './components/team/MeetTheTeam'; // Temporarily hidden
import Contact from './components/Contact';
import ApproachJourney from './components/approach/ApproachJourney';
import TechPartners from './components/TechPartners';
import DemosSection from './components/demos/DemosSection';
import Projects from './pages/Projects';
import ResourcesPage from './pages/ResourcesPage';
import ArticlePage from './pages/ArticlePage';
import CaseStudyPage from './pages/CaseStudyPage';

// Mobile Resource Pages
import MobileResourcesPage from './mobile/MobileResourcesPage';
import MobileArticlePage from './mobile/MobileArticlePage';
import MobileCaseStudyPage from './mobile/MobileCaseStudyPage';

// Demos Page
import DemosPage from './pages/DemosPage';
import MobileDemosPage from './mobile/MobileDemosPage';

import CapabilitiesTestPage from './pages/CapabilitiesTestPage';
import CapabilitiesV3TestPage from './pages/CapabilitiesV3TestPage';

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function HomePage() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  return (
    <SmoothScroll>
      {/* SEO for Homepage */}
      <SEO
        canonical="/"
        schema={homepageSchema}
      />
      {/* Progress Bar */}
      <motion.div
        style={{
          scaleX,
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: 'linear-gradient(90deg, #00D9FF 0%, #8B5CF6 50%, #FF6B9D 100%)',
          transformOrigin: '0%',
          zIndex: 9999
        }}
      />

      <Navbar />

      {/* Hero Section */}
      <Hero />

      {/* Capabilities Section (Three Pillars) */}
      <Capabilities />

      {/* Strategic Approach - 6 Steps - NEW COMPONENT */}
      <ApproachJourney  />

      {/* Tech Partners / Platforms Section */}
      <TechPartners />

      {/* Live Demos Section */}
      <DemosSection />

      {/* Team Section - Temporarily hidden */}
      {/* <MeetTheTeam /> */}

      {/* Contact Section */}
      <Contact />

      {/* Footer */}
      <footer style={{ 
        background: '#050816', 
        padding: '60px 20px', 
        borderTop: '1px solid rgba(148, 163, 184, 0.1)' 
      }}>
        <div className="container" style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '40px', 
            marginBottom: '40px' 
          }}>
            <div>
              <h3 className="gradient-text" style={{ 
                fontFamily: 'var(--font-heading)',
                fontSize: '24px', 
                marginBottom: '16px', 
                fontWeight: '700' 
              }}>
                Rosalytics
              </h3>
              <p style={{ color: '#9CA3AF', lineHeight: '1.6', fontSize: '15px' }}>
                Operational Intelligence for the Modern Enterprise
              </p>
            </div>

            <div>
              <h4 style={{ 
                fontFamily: 'var(--font-heading)',
                fontSize: '15px', 
                marginBottom: '16px', 
                fontWeight: '600',
                color: '#E5E7EB'
              }}>
                Capabilities
              </h4>
              <ul style={{ listStyle: 'none', color: '#9CA3AF', lineHeight: '2.2', fontSize: '14px' }}>
                <li>GenAI Solutions</li>
                <li>Manufacturing Analytics</li>
                <li>Custom Applications</li>
              </ul>
            </div>

            <div>
              <h4 style={{ 
                fontFamily: 'var(--font-heading)',
                fontSize: '15px', 
                marginBottom: '16px', 
                fontWeight: '600',
                color: '#E5E7EB'
              }}>
                Quick Links
              </h4>
              <ul style={{ listStyle: 'none', color: '#9CA3AF', lineHeight: '2.2', fontSize: '14px' }}>
                <li><a href="#capabilities" style={{ color: 'inherit', textDecoration: 'none' }}>Our Approach</a></li>
                <li><a href="#demos" style={{ color: 'inherit', textDecoration: 'none' }}>Live Demos</a></li>
                <li><a href="/resources" style={{ color: 'inherit', textDecoration: 'none' }}>Resources</a></li>
                {/* <li><a href="#team" style={{ color: 'inherit', textDecoration: 'none' }}>Team</a></li> */}{/* Temporarily hidden */}
                <li><a href="#contact" style={{ color: 'inherit', textDecoration: 'none' }}>Contact</a></li>
              </ul>
            </div>

            <div>
              <h4 style={{ 
                fontFamily: 'var(--font-heading)',
                fontSize: '15px', 
                marginBottom: '16px', 
                fontWeight: '600',
                color: '#E5E7EB'
              }}>
                Contact
              </h4>
              <ul style={{ listStyle: 'none', color: '#9CA3AF', lineHeight: '2.2', fontSize: '14px' }}>
                <li>Milwaukee, WI</li>
                <li>United States</li>
                <li style={{ marginTop: '8px', color: '#00D9FF' }}>info@rosalytics.com</li>
              </ul>
            </div>
          </div>

          <div style={{ 
            borderTop: '1px solid rgba(148, 163, 184, 0.1)', 
            paddingTop: '30px', 
            textAlign: 'center', 
            color: '#6B7280',
            fontSize: '14px'
          }}>
            <p>© 2025 Rosalytics. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </SmoothScroll>
  );
}

function App() {
  return (
    <HelmetProvider>
      <DeviceProvider>
        <Router>
        <ScrollToTop />
        <SpeedInsights />
        <Routes>
          {/* Home route uses DeviceRouter for responsive rendering */}
          <Route
            path="/"
            element={<DeviceRouter desktopComponent={HomePage} />}
          />
          <Route path="/projects" element={<Projects />} />
          <Route
            path="/resources"
            element={
              <DeviceRouter
                desktopComponent={ResourcesPage}
                mobileComponent={MobileResourcesPage}
              />
            }
          />
          <Route
            path="/articles/:slug"
            element={
              <DeviceRouter
                desktopComponent={ArticlePage}
                mobileComponent={MobileArticlePage}
              />
            }
          />
          <Route
            path="/case-studies/:slug"
            element={
              <DeviceRouter
                desktopComponent={CaseStudyPage}
                mobileComponent={MobileCaseStudyPage}
              />
            }
          />
          <Route
            path="/demos"
            element={
              <DeviceRouter
                desktopComponent={DemosPage}
                mobileComponent={MobileDemosPage}
              />
            }
          />
          <Route path="/capabilities-test" element={<CapabilitiesTestPage />} />
          <Route path="/capabilities-v3" element={<CapabilitiesV3TestPage />} />
        </Routes>
        </Router>
      </DeviceProvider>
    </HelmetProvider>
  );
}

export default App;